import { cn } from "@/lib/utils";
import React, { FocusEvent, useState, useRef, useEffect } from "react";
import Checkbox from "./Checkbox";

interface MultipleSelectDropDownProps {
  label: string;
  required?: boolean;
  optionsList: {
    id: number | string;
    label: string;
    icon?: any;
  }[];
  onChange: (selectedValues: (number | string)[]) => void;
  value: (number | string)[];
  errorMessage?: string;
  placeholder?: string;
  onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
}

const MultipleSelectDropDown = (props: MultipleSelectDropDownProps) => {
  const {
    label,
    required,
    optionsList,
    onChange,
    value,
    errorMessage,
    placeholder,
    onFocus,
    onBlur,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    setActive(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    setActive(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionToggle = (optionId: number | string) => {
    const newValue = value.includes(optionId)
      ? value.filter((id) => id !== optionId)
      : [...value, optionId];
    onChange(newValue);
  };

  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder || "Select options";
    }
    if (value.length === 1) {
      const option = optionsList.find((opt) => opt.id === value[0]);
      return option?.label || "1 option selected";
    }
    return `${value.length} options selected`;
  };

  return (
    <div ref={dropdownRef}>
      <div
        className={cn(
          "flex justify-center items-center relative h-[56px] rounded-[8px]",
          errorMessage
            ? "border border-border-token-error-subtle bg-bg-error-tertiary-default"  
            : active
              ? "border border-border-token-neutral-subtle bg-bg-neutral-secondary-default"
              : "border border-border-token-neutral-subtle bg-bg-neutral-secondary-default",
        )}
        onClick={handleToggle}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={placeholder || label}
      >
        <div className="flex-1 px-4 py-3 label-m-medium">
          <span
            className={cn(
              value.length === 0
                ? "text-fg-neutral-subtle"
                : "text-fg-neutral-strong",
              "block mt-2",
            )}
          >
            {getDisplayText()}
          </span>
        </div>

        {/* Custom arrow */}
        <div className="absolute right-4 pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={cn(
              "h-6 w-6 text-fg-neutral-subtle transition-transform duration-200",
              isOpen && "rotate-180",
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        <label
          className={[
            "absolute left-2 flex items-center px-2 transition-all duration-200 ease-in-out body-s-regular top-[2px] text-fg-neutral-strong",
            errorMessage
              ? ""
              : active
                ? "!text-fg-brand-subtle"
                : "text-fg-neutral-subtle",
          ].join("")}
        >
          {placeholder || label}
          {" "}
          {required && <span className="text-fg-brand-subtle">*</span>}
        </label>

        {/* Dropdown Options */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-bg-brand-secondary-default border border-border-token-neutral-minimal rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
            <div className="py-2" role="listbox">
              {optionsList.map((option) => (
                <div
                  key={option.id}
                  className="px-4 py-2 hover:bg-container-neutral-minimal"
                  role="option"
                  aria-selected={value.includes(option.id)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    label={option.label}
                    checked={value.includes(option.id)}
                    onCheck={() => handleOptionToggle(option.id)}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultipleSelectDropDown;
